import { useState } from 'react';
import { useLeaveBank } from '@/hooks/queries';
import { Card, ErrorNote, Spinner } from './ui';

/** Signed days: a grant may be negative, so "+-5" must not happen. */
const signed = (n: number) => `${n > 0 ? '+' : ''}${n}`;

/**
 * "Apr 2026". The year is not decoration: a leave year ending in March runs
 * April → March, so its ledger spans two calendar years and a bare "Jan" does
 * not say which one.
 */
const monthName = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
/** "April 2026" — the year matters here, since the bank restarts annually. */
const monthTitle = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * What an employee sees about their own paid leave: days available now, how
 * the year has gone, and a month-by-month ledger. No money, ever.
 */
export function LeaveBankCard({ month, userId }: { month: string; userId?: string }) {
  const { data, isLoading, error } = useLeaveBank(month, userId);
  const [showLedger, setShowLedger] = useState(false);

  if (isLoading) return <Card className="mt-4 flex justify-center p-6"><Spinner /></Card>;
  if (error) return <div className="mt-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  const t = data.totals;
  // The bank restarts each leave year, so accrual starts at the year start —
  // or at the joining month for someone who joined part-way through it. The
  // server already resolved that: months[0] is the first accruing month.
  const accrualStart = monthTitle(data.months[0]?.month ?? data.yearStart);
  // The year end is twelve months on from the year start, whatever month it is.
  const lastMonthOfYear = (() => {
    const [y, m] = data.yearStart.split('-').map(Number);
    const end = new Date(Date.UTC(y, m - 1 + 11, 1));
    return `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}`;
  })();
  const yearEnd = MONTHS[data.yearEndMonth - 1];

  return (
    <Card className="mt-4">
      <div className="flex flex-wrap items-center gap-4 px-5 py-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Paid leave available</p>
          <p className="text-3xl font-semibold tabular-nums text-brand-700">
            {data.available} <span className="text-base font-normal text-slate-500">day{data.available === 1 ? '' : 's'}</span>
          </p>
        </div>
        <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <div><dt className="inline text-slate-500">Accrued </dt><dd className="inline font-medium tabular-nums">+{t.accrued}</dd></div>
          {t.granted !== 0 && <div><dt className="inline text-slate-500">Granted </dt><dd className="inline font-medium tabular-nums text-brand-700">+{t.granted}</dd></div>}
          <div><dt className="inline text-slate-500">Used </dt><dd className="inline font-medium tabular-nums">−{t.used}</dd></div>
          {t.unpaid > 0 && <div><dt className="inline text-slate-500">Unpaid </dt><dd className="inline font-medium tabular-nums text-rose-700">{t.unpaid}</dd></div>}
        </dl>
        <p className="ml-auto max-w-xs text-xs text-slate-500">
          {data.paidLeavesPerMonth} day{data.paidLeavesPerMonth === 1 ? '' : 's'} added each month since {accrualStart}. Unused days carry
          forward to {yearEnd}{data.encashAtYearEnd ? ', when the balance is paid out' : ', then reset'}.
        </p>
        <button className="btn-ghost py-1 text-xs" onClick={() => setShowLedger((v) => !v)}>{showLedger ? 'Hide ledger' : 'Show ledger'}</button>
      </div>

      {showLedger && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="w-full">
            <caption className="border-b border-slate-100 bg-white px-3 py-2 text-left text-xs text-slate-500">
              Leave year {monthName(data.yearStart)} – {monthName(lastMonthOfYear)}. The bank restarts at zero after that.
            </caption>
            <thead className="bg-slate-50">
              <tr>
                <th className="th">Month</th>
                <th className="th text-right">Opening</th>
                <th className="th text-right">Accrued</th>
                <th className="th text-right">Granted</th>
                <th className="th text-right">Taken</th>
                <th className="th text-right">From bank</th>
                <th className="th text-right">Unpaid</th>
                <th className="th text-right">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.months.map((m) => (
                <tr key={m.month}>
                  <td className="td">{monthName(m.month)}</td>
                  <td className="td text-right tabular-nums text-slate-500">{m.opening}</td>
                  <td className="td text-right tabular-nums text-emerald-700">+{m.accrued}</td>
                  <td className={`td text-right tabular-nums ${m.granted ? 'text-brand-700' : 'text-slate-400'}`} title={m.grants.map((g) => `${g.days > 0 ? '+' : ''}${g.days} ${g.reason}`).join('\n')}>
                    {m.granted ? signed(m.granted) : '—'}
                  </td>
                  <td className="td text-right tabular-nums">{m.leaveUnits || '—'}</td>
                  <td className={`td text-right tabular-nums ${m.used ? 'text-rose-700' : 'text-slate-400'}`}>{m.used ? `−${m.used}` : '—'}</td>
                  <td className={`td text-right tabular-nums ${m.unpaid ? 'font-semibold text-rose-700' : 'text-slate-400'}`}>{m.unpaid || '—'}</td>
                  <td className="td text-right font-semibold tabular-nums">{m.closing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
